import EM from '@Ravnur-Inc/helpers/dom/em';
import $t from '@Ravnur-Inc/l10n/$t';
import RavnurPlayer from '@Ravnur-Inc/player';
import { Player$CombineOptions, Player$CrawlOptions } from '@Ravnur-Inc/player/src/types/Options';
import { Player$PlaylistMode } from '@Ravnur-Inc/player/src/types/Playlist';
import { Player$Source } from '@Ravnur-Inc/player/src/types/Source';
import { Player$State } from '@Ravnur-Inc/player/src/types/State';
import { Options, Vue, prop } from 'vue-class-component';
import { Watch } from '@Ravnur-Inc/decorators';

import { $Props } from '../../typings/tsx';

import translationKeys from './player.translation.keys';
import isEqual from '@Ravnur-Inc/nanoutils/isEqual';

import './player.scss';

const TABLET_WIDTH = 40;
const DEFAULT_TIME = 0;
const CN = 'player';

const DEFAULT_OPTIONS: Partial<Player$CombineOptions> = {
  flashPath: '/static/',
  hlsjsURL: '/static/hls.min.js',
  playlistforcepoint: TABLET_WIDTH * EM,
  playlistmode: 'right',
  loggerLevel: 0,
  startLevel: '-1',
  useHLSJSPlayer: true,
  showError: false,
  hls: {
    maxFragLookUpTolerance: 0.001,
    maxMaxBufferLength: 20,
    levelLoadingTimeOut: 100000,
    manifestLoadingTimeOut: 6000000,
  },
};

const STYLES = {
  rplaylistHeight: '33%',
};

class Props {
  sources!: Player$Source | Player$Source[];
  playlistTitle = prop({ default: '' });
  opts = prop<Partial<Player$CombineOptions>>({ default: {} });
  playerStyles = prop({ default: {} });
}

type Emits = {
  onStatechanged: (state: Player$State) => void;
  onTimeupdate: (time: number) => void;
  onChangesource: (source: Player$Source) => void;
  onPlay: () => void;
  onPause: () => void;
};

@Options({
  name: 'player',
  emits: ['statechanged', 'timeupdate', 'changesource', 'play', 'pause'],
  watch: {
    sources: {
      handler(this: Player) {
        this.refreshSources();
      },
    },
  },
})
export default class Player extends Vue.with(Props) {
  declare $props: $Props<Props, Emits>;

  private player: RavnurPlayer | null = null;
  public state: Player$State | null = null;
  private isPlaylist = false;
  private playlistMode: Player$PlaylistMode | null = null;

  private get cnsuffix(): string[] {
    const res = [];

    if (this.isPlaylist && this.sources instanceof Array && !this.sources.length) {
      res.push('empty');
    }

    const { state } = this;
    if (!state) {
      return res;
    }

    if (state.cc.lang && state.cc.location === 'below') {
      res.push(`b-${parseInt(state.cc.fontSize, 10)}`);
    }

    if (this.isPlaylist && this.playlistMode) {
      res.push(`playlist-${this.playlistMode}`);
    }
    return res;
  }

  private get options(): Partial<Player$CombineOptions> {
    const reducer = (memo: Dictionary<string>, key: string) => {
      memo[key] = $t('player', key);
      return memo;
    };
    const i18n = { ...translationKeys.reduce(reducer, {}), ...this.opts.i18n };
    return {
      playlistitle: this.playlistTitle,
      ...DEFAULT_OPTIONS,
      ...this.opts,
      i18n,
    };
  }

  @Watch('options.crawl', { immediate: true })
  onCrawlChange(updated: Player$CrawlOptions, old: Player$CrawlOptions) {
    const isSame = isEqual(updated, old);

    if (isSame) {
      return;
    }

    this.refreshCrawl();
  }

  @Watch('options.showCrawl')
  onCrawlToggled(visibility?: boolean) {
    try {
      this.player?.refreshCrawlExtension(this.options.crawl, !!visibility);
    } catch (e) {
      // noop
    }
  }

  public getCurrentTime(): number {
    const { player } = this;
    return (player && player.controller && player.controller.getCurrentTime()) || DEFAULT_TIME;
  }

  public setTime(time: number) {
    const { player } = this;
    if (player && player.controller) {
      player.controller.setCurrentTime(time, true);
    }
  }

  public pushState(state: Player$State) {
    const { player } = this;
    const old = this.state;

    if (!old || !player) {
      return;
    }

    if (state.cc.sources !== old.cc.sources) {
      player.bus.emit('ccsources', state.cc.sources, true);
      player.bus.emit('cclang', state.cc.lang);
    }
    if (state.cc.lang !== old.cc.lang) {
      player.bus.emit('cclang', state.cc.lang);
    }
    if (state.cc.timedata !== old.cc.timedata) {
      player.bus.emit('refreshcaptions', state.cc.timedata, state.cc.timedataLang);
    }

    if (state.toc.sources !== old.toc.sources) {
      player.bus.emit('tocsources', state.toc.sources);
    } else if (state.toc.lang !== old.toc.lang) {
      player.bus.emit('toclang', state.toc.lang);
    } else if (state.toc.timedata !== old.toc.timedata) {
      player.bus.emit('chaptersloaded', state.toc.timedata, state.toc.timedataLang);
    }
  }

  public playPart(from?: number, to?: number) {
    const { player } = this;
    if (!player) {
      return;
    }
    player.playPart(from || 0, to);
  }

  public setCurrentTime(from: number) {
    const { player } = this;
    if (!player) {
      return;
    }
    player.controller.setCurrentTime(from);
  }

  public pause(silent = false) {
    const { player } = this;
    if (!player || !player.controller) {
      return;
    }
    player.controller.pause(silent);
  }

  mounted() {
    if (this.sources) {
      this.refreshSources();
    }
  }

  beforeUnmount() {
    this.destroyIfNeeded();
  }

  render() {
    const { cnsuffix } = this;
    const cn = [CN].concat(cnsuffix.map((suffix) => `${CN}--${suffix}`));

    return (
      <div class={cn}>
        <div ref="container" class={`${CN}__container`} />
      </div>
    );
  }

  private refreshSources() {
    this.destroyIfNeeded();
    const sources = this.sources;

    this._initPlayer(sources);
  }

  private _initPlayer(sources: Player$Source | Player$Source[]) {
    const { container } = this.$refs;
    if (!(container instanceof HTMLElement)) {
      return;
    }
    container.removeAttribute('style');
    this.isPlaylist = sources instanceof Array;
    const styles = { ...STYLES, ...this.playerStyles };
    const player = new RavnurPlayer(container, styles);
    this.$emit('ready');
    this.player = player;
    player.bus.on('changeplaylistmode', (mode: Player$PlaylistMode) => (this.playlistMode = mode));
    player.bus.on('statechanged', (state: Player$State) => {
      this.state = state;
      this.$emit('statechanged', state);
    });
    player.on('timeupdate', () => {
      this.$emit('timeupdate', this.getCurrentTime());
    });
    player.on('changesource', (source: unknown) => {
      this.$emit('changesource', source);
    });
    player.on('play', () => this.$emit('play'));
    player.on('pause', () => this.$emit('pause'));

    player.setup(sources, this.options);

    this.$nextTick(this.setCrossOrigin);
  }

  protected refreshCrawl() {
    try {
      this.player?.refreshCrawlExtension(this.options.crawl, !!this.options.showCrawl);
    } catch (e) {
      // noop
    }
  }

  // RMP-4517 Can't take screenshots using canvas from videos with origin https://amsrmpdevwe-euwe.streaming.media.azure.net/...
  private setCrossOrigin() {
    const video = document.querySelector('video');

    if (!(video instanceof HTMLVideoElement)) {
      return;
    }

    video.setAttribute('crossorigin', 'Anonymous');
  }

  private destroyIfNeeded() {
    const player = this.player;
    if (player) {
      try {
        player.destroy();
      } catch (e) {
        //
      }
      this.player = null;
    }
  }
}
